import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import {Box, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';

import {ErrorMessage} from '../../../components/ErrorMessage/ErrorMessage';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import {MatchCountDisplay} from '../../../features/company/recruiting/matches/MatchCountDisplay/MatchCountDisplay';
import {MatchesBoardView} from '../../../features/company/recruiting/matches/MatchesBoardView/MatchesBoardView';
import {MatchesTableView} from '../../../features/company/recruiting/matches/MatchesTableView/MatchesTableView';
import {Walkthrough, WalkthroughTrigger} from '../../../features/walkthroughs';
import {companyMatchesWalkthrough, MATCHES_TOUR_TARGETS} from '../../../features/walkthroughs/steps/matches';
import {useMatchesBoardColumnsSorting} from '../../../hooks/useMatchesBoardColumnsSorting';
import {useMatchesSorting} from '../../../hooks/useMatchesSorting';
import {useTrackingEvents} from '../../../hooks/useTrackingEvents';
import {greenColors} from '../../../layouts/theme/designSystem';
import {MATCH_DETAILS_FOCUS_MATCH_PARAM} from '../../../lib/matchDetailsNav';
import {TrackingEvents} from '../../../types/TrackingEvents';
import {CompanyAdminRoleData} from '../../../types/User';
import {scheduleMatchBoardCardPulse} from '../../../utils/matchBoardCardPulse';

type MatchViewMode = 'table' | 'board';
const MATCHES_VIEW_QUERY_PARAM = 'view';

const isMatchViewMode = (value: string): value is MatchViewMode => value === 'table' || value === 'board';

export const MatchesPage = () => {
  const {currentUser} = useContext(UserContext);
  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = useContext(LanguageContext);
  const {t} = useTranslation('translation', {keyPrefix: 'matchesPage'});
  const [searchTerm, setSearchTerm] = useState('');
  const viewModeQueryValue = searchParams.get(MATCHES_VIEW_QUERY_PARAM);
  const viewMode: MatchViewMode =
    viewModeQueryValue && isMatchViewMode(viewModeQueryValue) ? viewModeQueryValue : 'board';
  const {Version, Action, Category} = TrackingEvents;
  const {dispatchTracking} = useTrackingEvents({
    version: Version.v1,
    action: Action.MatchesViewModeSelected,
    category: Category.MatchesPage,
  });

  const {
    matches: tableMatches,
    jobOptions,
    statusOptions,
    matchesSummaryCounts,
    matchesSummaryLoading,
    pagination: tablePagination,
    hasNext: tableHasNext,
    getNext: tableGetNext,
    loadingInitialData: isTableLoadingInitialData,
    matchesListError,
    matchesSummaryError,
    filterBy,
    initialJobs,
    initialStatuses,
    activeStatFilter,
  } = useMatchesSorting(companyId);
  const {
    columns: boardColumns,
    loadingInitialData: isBoardLoadingInitialData,
    error: boardError,
  } = useMatchesBoardColumnsSorting(companyId);

  const filteredMatches = useMemo(() => {
    if (!tableMatches) {
      return tableMatches;
    }

    const normalizedTerm = searchTerm.trim().toLowerCase();
    if (!normalizedTerm) {
      return tableMatches;
    }

    return tableMatches.filter((match) => {
      const firstName = match.applicant?.personalInformation?.firstName ?? '';
      const lastName = match.applicant?.personalInformation?.lastName ?? '';
      const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
      return fullName.includes(normalizedTerm);
    });
  }, [tableMatches, searchTerm]);
  const isInMemorySearchActive = searchTerm.trim().length > 0;

  const handleAllMatchesStatCardClick = useCallback(() => {
    filterBy({job: [], status: []});
  }, [filterBy]);

  const handlePendingStatCardClick = useCallback(() => {
    const acceptedOption = statusOptions.find((o) => o.id === 'accepted');
    if (!acceptedOption) return;
    filterBy({job: initialJobs, status: [acceptedOption]});
  }, [filterBy, initialJobs, statusOptions]);

  const handleInProgressStatCardClick = useCallback(() => {
    const status = statusOptions.filter((option) => option.id !== 'accepted');
    if (!status.length) return;
    filterBy({job: initialJobs, status});
  }, [filterBy, initialJobs, statusOptions]);

  const handleViewModeChange = useCallback(
    (_: unknown, newValue: MatchViewMode | null) => {
      if (!newValue) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(MATCHES_VIEW_QUERY_PARAM, newValue);
          return next;
        },
        {replace: true}
      );
      dispatchTracking({payload: {selectedMode: newValue}});
    },
    [dispatchTracking, setSearchParams]
  );

  const handleWalkthroughStepAfter = useCallback(
    ({step, data}: {step: {target: string}; data: {action: string}}) => {
      if (step.target !== MATCHES_TOUR_TARGETS.viewToggle || data.action !== 'next') {
        return;
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(MATCHES_VIEW_QUERY_PARAM, 'table');
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    const urlLang = searchParams.get('lang');
    const urlViewMode = searchParams.get(MATCHES_VIEW_QUERY_PARAM);
    const hasInvalidViewMode = Boolean(urlViewMode && !isMatchViewMode(urlViewMode));

    if (urlLang !== lang || hasInvalidViewMode) {
      const next = new URLSearchParams(searchParams);
      if (urlLang !== lang) {
        next.set('lang', lang);
      }
      if (hasInvalidViewMode) {
        next.set(MATCHES_VIEW_QUERY_PARAM, 'board');
      }
      setSearchParams(next, {replace: true});
    }
  }, [lang, searchParams, setSearchParams]);

  const focusMatchId = searchParams.get(MATCH_DETAILS_FOCUS_MATCH_PARAM);

  useEffect(() => {
    if (!focusMatchId) {
      return;
    }

    if (viewMode !== 'board') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(MATCHES_VIEW_QUERY_PARAM, 'board');
          return next;
        },
        {replace: true}
      );
      return;
    }

    const clearFocusParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(MATCH_DETAILS_FOCUS_MATCH_PARAM);
          return next;
        },
        {replace: true}
      );
    };

    const cancelPulse = scheduleMatchBoardCardPulse(focusMatchId, {
      onSettled: clearFocusParam,
    });

    return () => {
      cancelPulse();
    };
  }, [focusMatchId, setSearchParams, viewMode]);

  const hasError = Boolean(matchesSummaryError || (viewMode === 'table' ? matchesListError : boardError));
  if (hasError) {
    return <ErrorMessage />;
  }

  return (
    <Box sx={{maxWidth: 1120, mx: 'auto', width: '100%'}}>
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: 1.5,
          color: greenColors[700],
          mb: 0.5,
        }}
      >
        {t('breadcrumb')}
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3}}>
        <Typography
          component='h1'
          data-tour='matches.title'
          sx={{
            fontSize: '34px',
            lineHeight: 1.5,
            fontWeight: 700,
            color: greenColors[700],
          }}
        >
          {t('title')}
        </Typography>
        <WalkthroughTrigger walkthroughId='companyMatches' />
      </Box>

      <Box data-tour='matches.stats'>
        <MatchCountDisplay
          activeFilter={activeStatFilter}
          counts={matchesSummaryCounts}
          loading={matchesSummaryLoading && !matchesSummaryCounts}
          onAllMatchesClick={handleAllMatchesStatCardClick}
          onPendingCardClick={handlePendingStatCardClick}
          onInProgressCardClick={handleInProgressStatCardClick}
        />
      </Box>

      <Box sx={{mt: 3, mb: 2, display: 'flex', justifyContent: 'flex-end'}}>
        <ToggleButtonGroup
          exclusive
          value={viewMode}
          onChange={handleViewModeChange}
          size='small'
          data-tour='matches.viewToggle'
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '10px',
            overflow: 'hidden',
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: '0',
              px: 1.5,
              py: 0.75,
              display: 'flex',
              gap: 0.75,
              alignItems: 'center',
            },
            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
              borderLeft: '1px solid',
              borderLeftColor: 'divider',
            },
          }}
        >
          <ToggleButton value='board'>
            <ViewKanbanOutlinedIcon sx={{fontSize: 16}} />
            <Typography variant='body2'>{t('viewModes.board')}</Typography>
          </ToggleButton>
          <ToggleButton value='table'>
            <TableRowsOutlinedIcon sx={{fontSize: 16}} />
            <Typography variant='body2'>{t('viewModes.table')}</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'table' && (
        <MatchesTableView
          loadingInitialData={isTableLoadingInitialData}
          matches={filteredMatches}
          filterBy={filterBy}
          jobOptions={jobOptions}
          statusOptions={statusOptions}
          initialJobs={initialJobs}
          initialStatuses={initialStatuses}
          pagination={isInMemorySearchActive ? undefined : tablePagination}
          hasNext={isInMemorySearchActive ? false : tableHasNext}
          getNext={isInMemorySearchActive ? undefined : tableGetNext}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
        />
      )}
      {viewMode === 'board' && (
        <MatchesBoardView
          loadingInitialData={isBoardLoadingInitialData}
          filterBy={filterBy}
          jobOptions={jobOptions}
          statusOptions={statusOptions}
          initialJobs={initialJobs}
          initialStatuses={initialStatuses}
          columns={boardColumns}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
        />
      )}
      <Walkthrough
        definition={companyMatchesWalkthrough}
        enabled={!isBoardLoadingInitialData && !isTableLoadingInitialData}
        onStepAfter={handleWalkthroughStepAfter}
      />
    </Box>
  );
};
